import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from '@mui/material/FormControlLabel';
import OutlinedInput from "@mui/material/OutlinedInput";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../../api";
import { useAuth } from '../AuthContext'
import Notification from '../Notification';
import Error from "../Error/Error";
import Loading from "../Loading";

const subtitleStyles = {
    borderBottom: '1px solid grey', 
    textAlign: 'center', 
    pb: 2,
}

const boxStyles = {
    padding: 2, 
    mt: 2, 
    backgroundColor: '#fff', 
    borderRadius: '10px', 
    minHeight: '200px',
    width: '48%',
}

const USERNAME_MIN_LENGTH = process.env.REACT_APP_USERNAME_MIN_LENGTH || 5;
const USERNAME_MAX_LENGTH = process.env.REACT_APP_USERNAME_MAX_LENGTH || 20;
const PASSWORD_MIN_LENGTH = process.env.REACT_APP_PASSWORD_MIN_LENGTH || 8;
const PASSWORD_MAX_LENGTH = process.env.REACT_APP_PASSWORD_MAX_LENGTH || 20;
const CONDITION_ID_MIN_VALUE = process.env.REACT_APP_CONDITION_ID_MIN_VALUE || 1;
const CONDITION_ID_MAX_VALUE = process.env.REACT_APP_CONDITION_ID_MAX_VALUE || 100;

const usernameValidationSettings = {
    minLength: USERNAME_MIN_LENGTH,
    maxLength: USERNAME_MAX_LENGTH,
}

const passwordValidationSettings = {
    minLength: PASSWORD_MIN_LENGTH,
    maxLength: PASSWORD_MAX_LENGTH,
}

const conditionIdValidationSettings = {
    minValue: CONDITION_ID_MIN_VALUE,
    maxValue: CONDITION_ID_MAX_VALUE,
}

const AddParticipant = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [conditionId, setConditionId] = useState(null);
    const [hasAssignedVideos, setHasAssignedVideos] = useState(false);
    const [videos, setVideos] = useState([]);
    const [availableVideos, setAvailableVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [assignedVideos, setAssignedVideos] = useState(null);
    const [alert, setAlert] = useState(false);
    const [alertData, setAlertData] = useState({});

    const { isAdmin, authLoading } = useAuth();
    const navigate = useNavigate();

    useEffect( () => {
        const controller = new AbortController();
        const fetchVideos = async() => {
          try {
            const data = await api.get('/videos/all');
            setVideos(data);
            setAvailableVideos(data.map(v => v.id));
          } catch (err) {
            console.error("Error fetching videos:", err.message);
            setError(err.message);
          } finally {
            setLoading(false);
          }
        }

        fetchVideos();

        // Cleanup function to abort fetch on unmount
        return () => controller.abort();
    }, []);

    const addVideo = (id) => {
        if (!id) return;

        const video = videos.find(video => video.id === id);
        if (!video) {
            setAlertData({ severity: "error", message: "Video not found." });
            return;
        }

        if (assignedVideos && Object.values(assignedVideos).some(v => v === id)) {
            // Video already assigned, do not add again
            return;
        } else {
            const newOrder = Object.keys(assignedVideos || {}).length + 1;
            setAssignedVideos(prev => ({
                ...prev,
                [newOrder]: id
            }));
        }
        // Remove from available videos
        setAvailableVideos(prev => prev.filter(v => v !== id));
    }

    const removeVideo = (id) => {
        setAssignedVideos(prev => {
            const newAssigned = {...prev};
            // Remove the video and re-order the rest
            delete newAssigned[Object.keys(newAssigned).find(key => newAssigned[key] === id)];
            const orderedValues = Object.values(newAssigned);
            const reordered = {};
            orderedValues.forEach((vid, idx) => {
                reordered[idx + 1] = vid;
            });
            return reordered;
        });
        // Add back to available videos
        setAvailableVideos(prev => [...prev, id]);
    }

    const handleTogglePassword = () => setShowPassword((prev) => !prev);
    const handleToggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

    const validateUsername = (username) => {
        const re = /^[a-zA-Z0-9._-]{5,}$/;
        return re.test(username) && username.length >= usernameValidationSettings.minLength && username.length <= usernameValidationSettings.maxLength;
    }

    const submitForm = async (e) => {
        e.preventDefault();
        
        // Validate username
        if (!validateUsername(username)) {
            setAlertData({
                severity: "error",
                message: `Username can only contain letters and numbers. Spaces are not allowed. It must be at least ${usernameValidationSettings.minLength} characters long and at most ${usernameValidationSettings.maxLength} characters long.`
            });
            setAlert(true);
            return;
        }

        // Validate password
        if (password.length < passwordValidationSettings.minLength || password.length > passwordValidationSettings.maxLength) {
            setAlertData({
                severity: "error",
                message: `Password must be at least ${passwordValidationSettings.minLength} characters long and at most ${passwordValidationSettings.maxLength} characters long.`
            });
            setAlert(true);
            return;
        }

        // Validate confirm password
        if (password !== confirmPassword) {
            setAlertData({
                severity: "error",
                message: "Passwords do not match."
            });
            return;
        }

        // Validate condition ID
        if (!Number(conditionId) || Number(conditionId) < conditionIdValidationSettings.minValue || Number(conditionId) > conditionIdValidationSettings.maxValue) {
            setAlertData({
                severity: "error",
                message: `Condition ID must be a number between ${conditionIdValidationSettings.minValue} and ${conditionIdValidationSettings.maxValue}.`
            });
            setAlert(true);
            return;
        }

        // Validate assigned videos
        if (hasAssignedVideos && (!assignedVideos || Object.keys(assignedVideos).length === 0)) {
            setAlertData({
                severity: "error",
                message: "You must assign at least one video."
            });
            setAlert(true);
            return;
        }

        const participantData = {
            username,
            password,
            conditionId: Number(conditionId),
            assignedVideos: hasAssignedVideos ? assignedVideos : null
        };

        try {
            const response = await api.post('/participant/create', participantData);
            if (response.message) {
                // Reset form
                setUsername("");
                setPassword("");
                setConfirmPassword("");
                setConditionId(null);
                setHasAssignedVideos(false);
                setAssignedVideos(null);
                setAlertData({});
                setAlert(false);

                return navigate('/new-participant/success');
            }
        } catch (err) {
            console.error("Error creating participant:", err.message);
            setAlertData({
                severity: "error",
                message:  err.message || "Error creating participant. Please try again later."
            });
            setAlert(true);
        }
    }

    if (authLoading) {
        return <Loading />;
    }
    if (!isAdmin) {
        return <Error message="You do not have permission to view this page." />;
    }

    return (
        <div className="main">
            <Typography variant="h3" sx={{ textAlign: 'center', mb: 4 }}>Create Participant</Typography>
            {/* Alert message */}
            {alert && (
                <Notification
                    severity={alertData.severity} 
                    message={alertData.message}
                    onClose={() => {
                        setAlert(false);
                    }}
                    scroll={true}
                />
            )}
            <Box 
                component="form" 
                method="POST"
                onSubmit={submitForm} 
                sx={{ 
                    width: '80%', 
                    maxWidth: '600px', 
                    margin: '0 auto', 
                    pb: 4,
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="username" sx={{ mb: 2 }}>Participant ID (Username)</FormLabel>
                    <OutlinedInput
                        id="username"
                        name="username"
                        type="text"
                        onChange={(e) => setUsername(e.target.value)}
                        required
                    />
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="password" sx={{ mb: 2 }}>Password</FormLabel>
                    <OutlinedInput
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                onClick={handleTogglePassword}
                                edge="end"
                                aria-label="toggle password visibility"
                                >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="confirm-password" sx={{ mb: 2 }}>Confirm Password</FormLabel>
                    <OutlinedInput
                        id="confirm-password"
                        name="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                onClick={handleToggleConfirmPassword}
                                edge="end"
                                aria-label="toggle password visibility"
                                >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="conditionId" sx={{ mb: 2 }}>Condition ID</FormLabel>
                    <OutlinedInput
                        id="conditionId"
                        name="conditionId"
                        type="text"
                        onChange={(e) => setConditionId(e.target.value)}
                        required
                    />
                </FormControl>
                <FormControl>
                    <FormLabel htmlFor="assign-videos" sx={{ mb: 2 }}>Assign Videos</FormLabel>
                    <RadioGroup
                        row
                        id="assign-videos"
                        defaultValue="false"
                        name="assign-videos"
                        value={hasAssignedVideos}
                        onChange={(e) => setHasAssignedVideos(e.target.value === 'true')}
                    >
                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                        <FormControlLabel value="false" control={<Radio />} label="No" />
                    </RadioGroup>
                </FormControl>
                {/* Video Assignment */}
                {hasAssignedVideos && (
                    <Collapse in={hasAssignedVideos} unmountOnExit timeout={10000}>
                        <Box sx={{ 
                                border: '2px dotted var(--northwestern-purple)', 
                                borderRadius: '10px',
                                backgroundColor: 'var(--northwestern-purple-10)',
                                padding: 4,
                                mt: 2,
                            }}>
                            <Typography variant="h6" sx={{ textAlign: 'center' }}>Video Assignment</Typography>
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Select videos to assign and arrange them in order.
                            </Typography>
                            {loading ? (
                                <Typography variant="body1">Loading videos...</Typography>
                            ) : error ? (
                                <Typography variant="body1" color="error">{error}</Typography>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between'}}>
                                    <Box sx={boxStyles}>
                                        <Typography variant="subtitle1" sx={subtitleStyles}>Available Videos</Typography>
                                        <List>
                                            {availableVideos.length > 0 && availableVideos.map(id => {
                                                const video = videos.find(v => v.id === id);
                                                return (
                                                    <ListItem
                                                        key={video.id}
                                                        sx={{ borderRadius: '10px', bgcolor: 'rgb(228, 224, 238)', mb: 2, border: '1px solid rgb(118, 93, 160)' }}
                                                        secondaryAction={
                                                            <IconButton
                                                                edge="end"
                                                                aria-label="add"
                                                                onClick={() => addVideo(video.id)}
                                                            >
                                                                <AddCircleIcon sx={{ color: 'var(--northwestern-purple)' }} />
                                                            </IconButton>
                                                        }
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar alt={video.title} src={video.thumbnail_url || <SmartDisplayOutlinedIcon />} />
                                                        </ListItemAvatar>
                                                        <ListItemText primary={video.title} />
                                                    </ListItem>
                                                )}
                                            )}
                                        </List>
                                    </Box>
                                    <Box sx={boxStyles}>
                                        <Typography variant="subtitle1" sx={subtitleStyles}>Assigned Videos (in order)</Typography>
                                        {assignedVideos && Object.keys(assignedVideos).length > 0 ? (
                                            <List>
                                                {Object.entries(assignedVideos).map(([order, id]) => {
                                                    const video = videos.find(v => v.id === id);
                                                    return (
                                                        <ListItem
                                                            key={id}
                                                            sx={{ borderRadius: '10px', bgcolor: 'rgb(228, 224, 238)', mb: 2, border: '1px solid rgb(118, 93, 160)' }}
                                                            secondaryAction={
                                                                <IconButton
                                                                edge="end"
                                                                aria-label="add"
                                                                onClick={() => removeVideo(id)}
                                                            >
                                                                <DeleteOutlineIcon sx={{ color: 'var(--northwestern-purple)' }} />
                                                            </IconButton>
                                                        }
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar sx={{bgcolor: 'var(--northwestern-purple)'}}>{order}</Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText primary={video.title} />
                                                    </ListItem>
                                                )}
                                                )}
                                            </List>
                                        ) : (
                                            <Typography variant="body1" sx={{ width: '80%', margin: '0 auto', textAlign: 'center', color: 'grey', pt: 2 }}>
                                                No videos assigned yet. Click on the add icon to assign videos.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                )}
                {/* /Video Assignment */}
                <Button 
                    variant="contained" 
                    type="submit"
                    sx={{
                        alignSelf: 'center',
                        mt: 4

                    }}
                >
                    Create Participant
                </Button>
            </Box>
        </div>
    );
};

export default AddParticipant;